import React, { useMemo, useState } from "react";
import { env } from "@config";
import { getClassName } from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import { Role } from "@store/auth";
import { useFirebase, useMixpanel } from "@taskpane/context";
import { CrossIcon, LinkIcon } from "@taskpane/icons";
import { Dropdown } from "../Dropdown";
import { Tooltip, TooltipPosition, TooltipSize } from "../Tooltip";
import { MenuIcon } from "./icons";
import css from "./Header.module.scss";

export interface HeaderOption {
  id: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  label?: string;
  tooltip?: string;
  onClick?: () => void;
  render?: () => React.ReactNode;
}

export interface HeaderProps {
  className?: string;
  isLoading?: boolean;
  title?: string;
  options?: HeaderOption[];
  onActiveOptionChange?: (id: string | null) => void;
}

interface HeaderDropdownOption {
  id: string;
  label: string;
  className?: string;
  disabled?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  className,
  isLoading = false,
  title,
  options = [],
  onActiveOptionChange,
}) => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);

  const { signOut } = useFirebase();
  const { trackEvent } = useMixpanel();

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);

  const dropdownOptionGroups = useMemo<HeaderDropdownOption[][]>(
    () => [
      ...(user
        ? [
            [
              {
                id: "myMeetings",
                label: "Meetings",
                icon: LinkIcon,
                onClick: () => {
                  trackEvent("header.myMeetingsClick");
                  setIsDropdownOpened(false);
                  window.open(
                    `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}/meeting-records`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              },
              {
                id: "myRelationships",
                label: "Relationships",
                icon: LinkIcon,
                onClick: () => {
                  trackEvent("header.myRelationshipsClick");
                  setIsDropdownOpened(false);
                  window.open(
                    `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}/relationships`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              },
              {
                id: "performanceQ",
                label: "Performance Q",
                icon: LinkIcon,
                onClick: () => {
                  trackEvent("header.performanceQClick");
                  setIsDropdownOpened(false);
                  window.open(
                    `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}/performanceq`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              },
            ],
          ]
        : []),
      [
        ...(user
          ? [
              {
                id: "quickGuide",
                label: "Quick Guide",
                icon: LinkIcon,
                onClick: () => {
                  trackEvent("header.quickGuideClick");
                  setIsDropdownOpened(false);
                  window.open(
                    `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}/learning-hub`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              },
            ]
          : []),
        {
          id: "tutorials",
          label: "Tutorials",
          icon: LinkIcon,
          onClick: () => {
            trackEvent("header.tutorialsClick");
            setIsDropdownOpened(false);
            window.open(
              "https://help.substrata.me/en/collections/3602298-tutorials",
              "_blank",
              "noopener,noreferrer"
            );
          },
        },
        {
          id: "faq",
          label: "FAQ",
          icon: LinkIcon,
          onClick: () => {
            trackEvent("header.faqClick");
            setIsDropdownOpened(false);
            window.open(
              "https://help.substrata.me/en/collections/3600159-faq",
              "_blank",
              "noopener,noreferrer"
            );
          },
        },
      ],
      ...(user
        ? [
            [
              {
                id: "myAccount",
                label: "My Account",
                icon: LinkIcon,
                onClick: () => {
                  trackEvent("header.myAccountClick");
                  setIsDropdownOpened(false);
                  window.open(
                    `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              },
              ...(user.role !== Role.Dealmaker
                ? [
                    {
                      id: "adminSettings",
                      label: "Admin Settings",
                      icon: LinkIcon,
                      onClick: () => {
                        trackEvent("header.adminSettingsClick");
                        setIsDropdownOpened(false);
                        window.open(
                          `${env.accountOrigin}/orgs/${user?.orgId}/admin-settings`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      },
                    },
                  ]
                : []),
            ],
          ]
        : []),
      [
        {
          id: "helpCenter",
          label: "Help Center",
          icon: LinkIcon,
          onClick: () => {
            trackEvent("header.helpCenterClick");
            setIsDropdownOpened(false);
            window.open("https://help.substrata.me/en", "_blank", "noopener,noreferrer");
          },
        },
        {
          id: "serviceStatus",
          label: "Service Status",
          icon: LinkIcon,
          onClick: () => {
            trackEvent("header.serviceStatusClick");
            setIsDropdownOpened(false);
            window.open("https://substrata.statuspage.io", "_blank", "noopener,noreferrer");
          },
        },
        {
          id: "contactUs",
          label: "Contact Us",
          icon: LinkIcon,
          onClick: () => {
            trackEvent("header.contactUsClick");
            setIsDropdownOpened(false);
            window.open(
              "https://www.substrata.me/company/contact",
              "_blank",
              "noopener,noreferrer"
            );
          },
        },
        {
          id: "about",
          label: `Version ${env.appVersion}`,
          className: css.dropdownOptionVersion,
          disabled: true,
        },
        ...(user
          ? [
              {
                id: "logOut",
                label: "Log Out",
                className: css.dropdownOptionDanger,
                onClick: () => {
                  trackEvent("header.logOutClick");
                  setIsDropdownOpened(false);
                  signOut();
                },
              },
            ]
          : []),
      ],
    ],
    [dispatch, user, signOut]
  );

  const renderDropdownOption = (option: HeaderDropdownOption) => {
    const { id, label, icon: IconComponent, className, onClick, disabled = false } = option;
    const attrs = {
      dropdownOption: {
        disabled,
        onClick,
        key: id,
        type: "button" as const,
        className: getClassName(css.dropdownOption, className),
      },
    };

    return (
      <button {...attrs.dropdownOption}>
        <span>{label}</span>
        {IconComponent ? <IconComponent /> : null}
      </button>
    );
  };

  const renderDropdownOptionsGroup = (group: HeaderDropdownOption[], index: number) => {
    const attrs = {
      dropdownOptionsGroup: {
        key: index,
        className: css.dropdownOptionsGroup,
      },
    };

    return (
      <div {...attrs.dropdownOptionsGroup}>
        {group.map((option) => renderDropdownOption(option))}
      </div>
    );
  };

  const renderDropdownContent = () => {
    const attrs = {
      dropdownOptions: {
        className: css.dropdownOptions,
      },
    };

    return (
      <div {...attrs.dropdownOptions}>
        {dropdownOptionGroups.map((group, index) => renderDropdownOptionsGroup(group, index))}
      </div>
    );
  };

  const attrs = {
    container: {
      className: getClassName(css.container, isLoading && css.containerLoading, className),
    },
    titleRow: {
      className: css.titleRow,
    },
    title: {
      className: css.title,
    },
    buttonsWrapper: {
      className: css.buttonsWrapper,
    },
    dropdown: {
      isOpened: isDropdownOpened,
      positionOffset: 4,
      width: 172,
      onClose: () => {
        setIsDropdownOpened(false);
      },
      renderContent: renderDropdownContent,
    },
    menuButton: {
      type: "button" as const,
      className: getClassName(css.button, isDropdownOpened && css.buttonActive),
      onClick: () => {
        setIsDropdownOpened((prevState) => !prevState);
      },
    },
    menuButtonIcon: {
      className: css.buttonIcon,
    },
  };

  const renderOptionButtons = () =>
    options.map(({ id, icon, disabled, label, tooltip, onClick }) => {
      const isActive = activeOptionId === id;
      const isDisabled = typeof disabled === "boolean" ? disabled : isLoading;
      const IconComponent = isActive ? CrossIcon : icon;

      const attrs = {
        tooltip: {
          contentClassName: css.buttonTooltip,
          disabled: isDisabled,
          size: TooltipSize.XS,
          text: tooltip,
          arrow: false,
          position: TooltipPosition.BottomEnd,
          positionOffset: 4,
          width: 290,
        },
        button: {
          type: "button" as const,
          className: css.button,
          disabled: isDisabled,
          onClick: () => {
            if (onClick) {
              onClick();
              return;
            }

            setActiveOptionId(isActive ? null : id);

            if (onActiveOptionChange) {
              onActiveOptionChange(isActive ? null : id);
            }
          },
        },
        buttonIcon: {
          className: css.buttonIcon,
        },
      };

      if (tooltip) {
        return (
          <Tooltip {...attrs.tooltip} key={id}>
            <button {...attrs.button}>
              {label ? <span>{label}</span> : null}
              <IconComponent {...attrs.buttonIcon} />
            </button>
          </Tooltip>
        );
      }

      return (
        <button {...attrs.button} key={id}>
          {label ? <span>{label}</span> : null}
          <IconComponent {...attrs.buttonIcon} />
        </button>
      );
    });

  const renderActiveOptionContent = () => {
    if (!activeOptionId) {
      return null;
    }

    const { render } = options.find((option) => option.id === activeOptionId) || {};

    return render ? render() : null;
  };

  return (
    <div {...attrs.container}>
      <div {...attrs.titleRow}>
        {title ? <span {...attrs.title}>{title}</span> : null}
        <div {...attrs.buttonsWrapper}>
          {renderOptionButtons()}
          <Dropdown {...attrs.dropdown}>
            <button {...attrs.menuButton}>
              <MenuIcon {...attrs.menuButtonIcon} />
            </button>
          </Dropdown>
        </div>
      </div>
      {renderActiveOptionContent()}
    </div>
  );
};

export default Header;
